import { format, parseISO } from 'date-fns';

import type { PartnerCustomer } from '@/api/partners/organizations/types';
import DataTable from '@/ui/data-table';

import { ClickableStatusIcon } from './clickable-status-icon';
import { StatusIcon } from './status-icon';

export type DocumentCategory = 'license' | 'insurance';

type Props = {
  customers: PartnerCustomer[];
  page: number;
  total: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onDocumentsPress: (customerId: number, category: DocumentCategory) => void;
};

const COLUMNS = [
  { key: 'name', title: 'CUSTOMER NAME', style: { minWidth: 180 } },
  {
    key: 'email',
    title: 'CONTACT EMAIL',
    style: { minWidth: 220, maxWidth: 220 },
    truncate: true,
  },
  { key: 'phone', title: 'PHONE', style: { minWidth: 140 } },
  { key: 'dob', title: 'DOB', style: { minWidth: 120 } },
  {
    key: 'createdAt',
    title: 'CUSTOMER ADDED',
    style: { minWidth: 200, maxWidth: 200 },
    truncate: true,
  },
  { key: 'license', title: 'LICENSE', style: { minWidth: 90 } },
  { key: 'insurance', title: 'INSURANCE', style: { minWidth: 100 } },
  { key: 'approved', title: 'APPROVED TO DRIVE', style: { minWidth: 150 } },
];

// parseISO treats date-only strings (dob) as local midnight; `new Date()`
// would treat them as UTC and shift the day back in US timezones.
const formatDob = (value: string | null) => {
  if (!value) return '';

  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MM/dd/yyyy');
};

const formatCreatedAt = (value: string) => {
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : format(parsed, 'MMMM d, yyyy | h:mm a');
};

export const CustomersTable = ({
  customers,
  page,
  total,
  rowsPerPage,
  onPageChange,
  onDocumentsPress,
}: Props) => {
  const rows = customers.map((customer) => ({
    key: customer.id,
    id: customer.id,
    name: `${customer.firstName} ${customer.lastName}`,
    email: customer.email,
    phone: customer.phone,
    dob: formatDob(customer.dob),
    createdAt: formatCreatedAt(customer.createdAt),
    license:
      customer.licenseStatus === 'verified' ? (
        <ClickableStatusIcon
          onPress={() => onDocumentsPress(customer.id, 'license')}
        />
      ) : (
        <StatusIcon status={customer.licenseStatus} />
      ),
    insurance:
      customer.insuranceStatus === 'verified' ? (
        <ClickableStatusIcon
          onPress={() => onDocumentsPress(customer.id, 'insurance')}
        />
      ) : (
        <StatusIcon status={customer.insuranceStatus} />
      ),
    approved: <StatusIcon status={customer.approvedStatus} />,
  }));

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      page={page}
      total={total}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
    />
  );
};
